import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { CareElementGridHelper } from "@/Flowsheets/Helpers/CareElementGridHelper";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { ImmunizationCareElement } from "@/Flowsheets/Models/CareElements/ImmunizationCareElement";
import CareComponentNotes from "./CareComponentParts/CareComponentNotes";
import { GridCell } from "./CareComponentParts/GridCell";
import { ICareComponentProps } from "./Types";

import styles from "./ImmunizationsCareComponent.module.scss";

interface ImmunizationsCareComponent extends CareComponent {
    CareElements: ImmunizationCareElement[];
}

function ImmunizationCareElementRow({ careElement }: Readonly<{ careElement: ImmunizationCareElement }>): JSX.Element {
    const { getText } = useTranslation();
    return (
        <>
            <div><label className={styles.rowLabel}>{getText(`Flowsheets_ImmunizationsCareComponentLabel_${careElement.Name}`)}</label></div>
            <div className={styles.radioButtonContainer}>
                <input
                    type="radio"
                    id="immunization-status-yes"
                    checked={careElement.IsRefused === false}
                    disabled
                />
                <label htmlFor="immunization-status-yes">
                    {getText("YesLabel")}
                </label>
            </div>
            <div className={styles.radioButtonContainer}>
                <input
                    type="radio"
                    id="immunization-status-no"
                    checked={careElement.IsRefused}
                    disabled
                />
                <label htmlFor="immunization-status-no">
                    {getText("NoLabel")}
                    {careElement.IsRefused && (
                        <span className={styles.refusedText}>
                            {` (${getText("Flowsheets_ImmunizationsCareComponentLabel_Refused")})`}
                        </span>
                    )}
                </label>
            </div>
            <div className={styles.frequencyContainer}>
                <GridCell
                    id="riskFactors-erectile-function"
                    value={CareElementGridHelper.handleFrequencyLabel(careElement.Frequency, getText)}
                    showAlert={careElement.IsOverdue}
                    alertVariant="warning"
                    alertTooltipText={getText("Flowsheets_OutsideFrequencyTooltipLabel")}
                />
            </div>
            <div>
                {careElement.Date && LuxonDateTimeParser.parse(careElement.Date).toFormat("yyyy-MM-dd")}
            </div>
        </>
    );
}

const ImmunizationsCareComponent = React.memo(
    (({ isEditable = false, state, mutateState }: Readonly<ICareComponentProps<ImmunizationsCareComponent>>): JSX.Element => {
        const { getText } = useTranslation();

        return (
            <div>
                <div className={styles.ImmunizationContainer}>
                    <div className={`${styles.headerLabel} ${styles.grid4}`}>
                        {getText("FrequencyLabel")}
                    </div>
                    <div className={`${styles.headerLabel}`}>
                        {getText("DateLabel")}
                    </div>
                    {state.CareElements.map(careElement => (
                        <ImmunizationCareElementRow
                            key={careElement.Name}
                            careElement={careElement}
                        />
                    ))}
                </div>
                <CareComponentNotes
                    id={`ImmunizationsNotes-${state.Id}`}
                    value={state.Note}
                    isEditable={isEditable}
                    onTextChange={newValue => mutateState(s => s.Note = newValue)}
                />
            </div>
        );
    })
);

export { ImmunizationsCareComponent };
